import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from "react";
import {
  Globe2,
  Plus,
  Search,
  Smartphone,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { publicApexHost } from "../lib/api";
import { suggestSlug } from "../marketing/lib/start-trial";

import {
  createWorkspace,
  WorkspaceApiError,
  type WorkspaceListItem,
} from "./api";
import {
  workspaceActivityLabel,
  workspaceArtifactLabel,
  workspaceDestination,
  workspaceStatusLabel,
} from "./presentation";

export function workspaceUrl(workspace: WorkspaceListItem): string {
  return `/workspaces/${encodeURIComponent(workspace.id)}`;
}

export function WorkspacePicker({
  workspaces,
}: {
  workspaces: WorkspaceListItem[];
}): ReactElement {
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Projects — Chill";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const sortedWorkspaces = useMemo(
    () =>
      [...workspaces].sort(
        (left, right) =>
          compareDateDescending(
            left.lastActivityAt ?? left.createdAt,
            right.lastActivityAt ?? right.createdAt,
          ) || left.name.localeCompare(right.name),
      ),
    [workspaces],
  );
  const filteredWorkspaces = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (!needle) return sortedWorkspaces;
    return sortedWorkspaces.filter((workspace) =>
      [
        workspace.name,
        workspace.id,
        workspaceDestination(workspace),
        workspaceArtifactLabel(workspace),
        workspaceStatusLabel(workspace),
      ].some((value) => value.toLocaleLowerCase().includes(needle)),
    );
  }, [query, sortedWorkspaces]);

  return (
    <div className="min-h-full text-[#20272b]">
        <main className="mx-auto w-full max-w-[92rem] px-4 py-8 sm:px-8 sm:py-12 xl:px-10">
          <div className="flex flex-col gap-6 border-b border-[#cfe4f1] pb-7 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-3xl font-semibold tracking-[-0.04em] sm:text-[2.5rem]">
                Projects
              </h1>
              <p className="mt-2 text-sm text-[#6f716b]">
                Your websites and apps, all in one place.
              </p>
            </div>

            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
              {workspaces.length > 0 ? (
                <div className="relative w-full sm:w-72">
                <label htmlFor="workspace-search" className="sr-only">
                  Search projects
                </label>
                <Search
                  className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-[#85877f]"
                  aria-hidden="true"
                />
                <Input
                  id="workspace-search"
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.currentTarget.value)}
                  placeholder="Search projects"
                  className="h-11 rounded-full border-[#c9e0ee] bg-white/80 pl-10 shadow-none backdrop-blur-sm focus-visible:ring-[#b8b9dd]"
                />
                </div>
              ) : null}
              <Button
                type="button"
                className="h-11 shrink-0 rounded-full bg-[#dceffc] px-5 text-[#20272b] shadow-none hover:bg-[#c8e6fa]"
                onClick={() => setCreating(true)}
              >
                <Plus aria-hidden="true" />
                New site
              </Button>
            </div>
          </div>

          {workspaces.length === 0 ? (
            <EmptyWorkspaces onNewSite={() => setCreating(true)} />
          ) : filteredWorkspaces.length === 0 ? (
            <EmptySearch query={query} onClear={() => setQuery("")} />
          ) : (
            <section aria-labelledby="workspace-results-heading">
              <div className="flex items-center justify-between py-5">
                <h2
                  id="workspace-results-heading"
                  className="text-xs font-medium text-[#7a7c75]"
                >
                  {filteredWorkspaces.length}{" "}
                  {filteredWorkspaces.length === 1
                    ? "project"
                    : "projects"}
                </h2>
                <span className="text-xs text-[#92948d]">Recent activity</span>
              </div>
              <ul className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,24rem),1fr))] gap-5">
                {filteredWorkspaces.map((workspace) => (
                  <WorkspaceCard key={workspace.id} workspace={workspace} />
                ))}
              </ul>
            </section>
          )}
        </main>
      {creating ? (
        <NewSiteDialog onClose={() => setCreating(false)} />
      ) : null}
    </div>
  );
}

function WorkspaceCard({
  workspace,
}: {
  workspace: WorkspaceListItem;
}): ReactElement {
  const statusTone =
    workspace.artifact.status === "live"
      ? "bg-[#e2eee9] text-[#315f54]"
      : workspace.artifact.status === "testing"
        ? "bg-[#e8edf5] text-[#50647d]"
        : "bg-[#efefeb] text-[#6d7069]";
  const activityAt = workspace.lastActivityAt ?? workspace.createdAt;

  return (
    <li className="group relative overflow-hidden rounded-2xl border border-[#cfe4f1] bg-white/90 shadow-[0_8px_28px_rgba(91,139,174,0.08)] backdrop-blur-sm transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-[#a9cfe6] hover:shadow-[0_18px_46px_rgba(91,139,174,0.15)]">
      <a
        href={workspaceUrl(workspace)}
        className="absolute inset-0 z-20 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#76928b]"
      >
        <span className="sr-only">Open {workspace.name}</span>
      </a>

      <WorkspacePreview workspace={workspace} />

      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold tracking-[-0.015em]">
              {workspace.name}
            </h3>
            <p className="mt-1 truncate text-xs text-[#85877f]">
              {workspaceDestination(workspace)}
            </p>
          </div>
          <span
            className={cn(
              "shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium",
              statusTone,
            )}
          >
            {workspaceStatusLabel(workspace)}
          </span>
        </div>

        <div className="mt-5 flex items-center justify-between gap-3 border-t border-[#efefeb] pt-3.5 text-xs text-[#7b7d76]">
          <span className="flex items-center gap-1.5">
            {workspace.artifact.kind === "website" ? (
              <Globe2 className="size-3.5" aria-hidden="true" />
            ) : (
              <Smartphone className="size-3.5" aria-hidden="true" />
            )}
            {workspaceArtifactLabel(workspace)}
          </span>
          <time dateTime={activityAt} title={activityTitle(activityAt)}>
            {workspaceActivityLabel(activityAt)}
          </time>
        </div>
      </div>
    </li>
  );
}

function WorkspacePreview({
  workspace,
}: {
  workspace: WorkspaceListItem;
}): ReactElement {
  const container = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(
    () => typeof IntersectionObserver === "undefined",
  );

  useEffect(() => {
    if (shouldLoad || !workspace.previewUrl || !container.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setShouldLoad(true);
        observer.disconnect();
      },
      { rootMargin: "320px" },
    );
    observer.observe(container.current);
    return () => observer.disconnect();
  }, [shouldLoad, workspace.previewUrl]);

  return (
    <div
      ref={container}
      className="relative aspect-[16/10] overflow-hidden border-b border-[#d8e9f3] bg-[#edf7fd]"
    >
      {workspace.previewUrl && shouldLoad ? (
        <iframe
          src={workspace.previewUrl}
          title={`${workspace.name} preview`}
          loading="lazy"
          sandbox=""
          referrerPolicy="no-referrer"
          aria-hidden="true"
          tabIndex={-1}
          className="pointer-events-none absolute left-0 top-0 h-[200%] w-[200%] origin-top-left scale-50 border-0 bg-white"
        />
      ) : workspace.previewUrl ? (
        <div className="absolute inset-0 animate-pulse bg-[linear-gradient(120deg,#e7f3fa_15%,#f8fcff_45%,#e7f3fa_75%)] bg-[length:220%_100%]" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[radial-gradient(circle_at_50%_35%,#ffffff,#e5f3ff)] text-[#526b7b]">
          <span className="flex size-11 items-center justify-center rounded-2xl border border-white/70 bg-white/70 shadow-sm">
            {workspace.artifact.kind === "website" ? (
              <Globe2 className="size-5" aria-hidden="true" />
            ) : (
              <Smartphone className="size-5" aria-hidden="true" />
            )}
          </span>
          <span className="mt-3 text-xs font-medium">Preview not ready</span>
        </div>
      )}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/10 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
    </div>
  );
}

function EmptySearch({
  query,
  onClear,
}: {
  query: string;
  onClear: () => void;
}): ReactElement {
  return (
    <div className="py-24 text-center">
      <p className="text-sm font-medium">No projects match “{query.trim()}”</p>
      <button
        type="button"
        className="mt-2 text-sm text-[#4f746b] underline-offset-4 hover:underline"
        onClick={onClear}
      >
        Clear search
      </button>
    </div>
  );
}

function EmptyWorkspaces({
  onNewSite,
}: {
  onNewSite: () => void;
}): ReactElement {
  return (
    <div className="mx-auto mt-16 max-w-xl rounded-2xl border border-[#cfe4f1] bg-white/85 p-10 text-center shadow-[0_16px_44px_rgba(91,139,174,0.1)] backdrop-blur-sm">
      <div className="mx-auto flex size-11 items-center justify-center rounded-full bg-[#dceffc] text-xl text-[#20272b]">
        +
      </div>
      <h2 className="mt-5 text-lg font-semibold">Start your first project</h2>
      <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-[#73766e]">
        Tell Chill what you want to make, then refine it through conversation.
      </p>
      <Button
        type="button"
        className="mt-6 rounded-full bg-[#dceffc] text-[#20272b] shadow-none hover:bg-[#c8e6fa]"
        onClick={onNewSite}
      >
        Create a site
      </Button>
    </div>
  );
}

function NewSiteDialog({ onClose }: { onClose: () => void }): ReactElement {
  const [businessName, setBusinessName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const apexHost = publicApexHost();

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const workspace = await createWorkspace({
        businessName: businessName.trim(),
        slug: slug.trim().toLowerCase(),
      });
      window.location.assign(workspaceUrl(workspace));
    } catch (caught) {
      if (caught instanceof WorkspaceApiError && caught.status === 403) {
        window.location.assign("/signup");
        return;
      }
      setError(
        caught instanceof WorkspaceApiError && caught.message === "slug_taken"
          ? "That address is already taken."
          : caught instanceof Error
            ? caught.message
            : "Couldn't create the site.",
      );
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#20272b]/20 px-4 backdrop-blur-[2px]"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-labelledby="new-site-title"
        className="w-full max-w-md rounded-2xl border border-[#cfe4f1] bg-[#fafdff] p-6 shadow-[0_24px_70px_rgba(91,139,174,0.2)]"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="new-site-title" className="text-lg font-semibold">
          New site
        </h2>
        <p className="mt-1 text-sm leading-6 text-[#6f716b]">
          Uses the same credits as your other sites. No extra checkout.
        </p>
        <form className="mt-5 space-y-4" onSubmit={(event) => void onSubmit(event)}>
          <div className="space-y-1.5">
            <Label htmlFor="new-site-name">Business name</Label>
            <Input
              id="new-site-name"
              value={businessName}
              onChange={(event) => {
                const next = event.currentTarget.value;
                setBusinessName(next);
                if (!slugTouched) setSlug(suggestSlug(next));
              }}
              placeholder="The bakery"
              required
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-site-slug">Site address</Label>
            <Input
              id="new-site-slug"
              value={slug}
              onChange={(event) => {
                setSlugTouched(true);
                setSlug(event.currentTarget.value.toLowerCase());
              }}
              placeholder="the-bakery"
              required
            />
            <p className="text-xs text-[#8b8d86]">
              Lives at{" "}
              <code className="rounded bg-[#f4f4f1] px-1.5 py-0.5">
                {slug || "yourname"}.{apexHost}
              </code>
            </p>
          </div>
          {error ? <p className="text-sm text-[#9b3d32]">{error}</p> : null}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="rounded-full bg-[#dceffc] text-[#20272b] shadow-none hover:bg-[#c8e6fa]"
              disabled={saving || !businessName.trim() || !slug.trim()}
            >
              {saving ? "Creating…" : "Create site"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function compareDateDescending(left?: string, right?: string): number {
  return (right ?? "").localeCompare(left ?? "");
}

function activityTitle(timestamp?: string): string | undefined {
  if (!timestamp) return undefined;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return undefined;
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}
